/* Erstwhile theme — vanilla JS, no build step.
   Each interactive part is a small custom element so sections keep working
   when they are re-rendered in the theme editor or loaded later. */
(() => {
  'use strict';

  const theme = (window.theme = window.theme || {});
  const routes = theme.routes || {};
  const strings = theme.strings || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------------- utils */
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const debounce = (fn, wait = 250) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  const formatMoney = (cents, format = theme.moneyFormat || '€{{amount}}') => {
    const value = Number(cents) / 100;
    const withDelimiters = (precision, thousands, decimal) => {
      const fixed = value.toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? grouped + decimal + fraction : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount':
          return withDelimiters(2, ',', '.');
        case 'amount_no_decimals':
          return withDelimiters(0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return withDelimiters(0, ' ', ',');
        case 'amount_with_space_separator':
          return withDelimiters(2, ' ', ',');
        default:
          return withDelimiters(2, ',', '.');
      }
    });
  };
  theme.formatMoney = formatMoney;

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const focusable = (root) =>
    $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])', root).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );

  const trapFocus = (container) => {
    const handler = (event) => {
      if (event.key !== 'Tab') return;
      const items = focusable(container);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  };

  /* BSS B2B Solution listens for cart changes to re-apply dealer prices. */
  const notifyDealerPricing = (type = 'CartMutate', detail = 'CartPriceMutate') => {
    try {
      const subject = window.BSS_B2B && window.BSS_B2B.observer && window.BSS_B2B.observer.cartSubject;
      if (subject && typeof subject.notifyObserver === 'function') subject.notifyObserver(type, detail);
    } catch (error) {
      /* the app is optional */
    }
  };

  /* -------------------------------------------------------------- drawers */
  class DrawerPanel extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(opener) {
      if (this.isOpen) return;
      $$('drawer-panel.is-open').forEach((drawer) => drawer !== this && drawer.close(false));
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');
      this.releaseFocus = trapFocus(this);
      $$(`[data-drawer-open="${this.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'true'));
      const target = $('[data-drawer-focus]', this) || $('.drawer__close', this) || focusable(this)[0];
      if (target) setTimeout(() => target.focus({ preventScroll: true }), 60);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close(restoreFocus = true) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (!$('drawer-panel.is-open')) document.body.classList.remove('is-locked');
      if (this.releaseFocus) this.releaseFocus();
      $$(`[data-drawer-open="${this.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'false'));
      if (restoreFocus && this.opener && typeof this.opener.focus === 'function') this.opener.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
  }
  define('drawer-panel', DrawerPanel);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open(opener);
  });

  /* -------------------------------------------------------------- dialogs */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.dialogOpen);
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        dialog.showModal();
      }
      return;
    }
    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) dialog.close();
      return;
    }
    /* click on the backdrop */
    if (event.target instanceof HTMLDialogElement && event.target.classList.contains('dialog')) {
      event.target.close();
    }
  });

  class TabGroup extends HTMLElement {
    connectedCallback() {
      this.tabs = $$('[role="tab"]', this);
      this.panels = $$('[role="tabpanel"]', this);
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          const next = (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        });
      });
    }

    select(index) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
    }
  }
  define('tab-group', TabGroup);

  /* --------------------------------------------------------------- header */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.shopify-section') || this;
      this.behavior = this.dataset.behavior || 'scroll-up';
      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      if (this.behavior === 'none') {
        this.wrapper.style.position = 'relative';
      } else {
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
      this.setupMega();
      this.setHeight();
      window.addEventListener('resize', debounce(() => this.setHeight(), 150));
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    setHeight() {
      const header = $('.header', this);
      if (header) document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        this.wrapper.classList.toggle('is-scrolled', y > 8);
        if (this.behavior === 'scroll-up') {
          const menuOpen = $('.header__menu-item.is-open', this);
          const hide = y > 240 && y > this.lastY && !menuOpen;
          const show = y < this.lastY - 4 || y <= 240;
          if (hide) this.setHidden(true);
          else if (show) this.setHidden(false);
        }
        this.lastY = y;
        this.ticking = false;
      });
    }

    setHidden(hidden) {
      this.wrapper.classList.toggle('is-hidden', hidden);
      document.body.classList.toggle('header-hidden', hidden);
    }

    setupMega() {
      const items = $$('[data-mega-parent]', this);
      const closeAll = (except) => {
        items.forEach((item) => {
          if (item === except) return;
          item.classList.remove('is-open');
          const link = $('.header__menu-link', item);
          if (link) link.setAttribute('aria-expanded', 'false');
        });
      };

      items.forEach((item) => {
        const link = $('.header__menu-link', item);
        let openTimer;
        let closeTimer;
        const open = () => {
          clearTimeout(closeTimer);
          closeAll(item);
          item.classList.add('is-open');
          link.setAttribute('aria-expanded', 'true');
        };
        const close = () => {
          clearTimeout(openTimer);
          item.classList.remove('is-open');
          link.setAttribute('aria-expanded', 'false');
        };

        item.addEventListener('mouseenter', () => {
          clearTimeout(closeTimer);
          openTimer = setTimeout(open, 90);
        });
        item.addEventListener('mouseleave', () => {
          clearTimeout(openTimer);
          closeTimer = setTimeout(close, 160);
        });
        item.addEventListener('focusin', open);
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) close();
        });
        item.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            close();
            link.focus();
          }
        });
        /* touch screens without hover: first tap opens, second tap follows the link */
        link.addEventListener('click', (event) => {
          if (window.matchMedia('(hover: none)').matches && !item.classList.contains('is-open')) {
            event.preventDefault();
            open();
          }
        });

        const image = $('.mega__image', item);
        if (image) {
          const original = { src: image.currentSrc || image.src, srcset: image.getAttribute('srcset') };
          const swap = (src, srcset) => {
            if (image.dataset.current === src) return;
            image.dataset.current = src;
            image.classList.add('is-switching');
            const next = new Image();
            next.onload = next.onerror = () => {
              if (image.dataset.current !== src) return;
              if (srcset) image.setAttribute('srcset', srcset);
              else image.removeAttribute('srcset');
              image.src = src;
              image.classList.remove('is-switching');
            };
            next.src = src;
          };
          $$('[data-mega-image]', item).forEach((megaLink) => {
            megaLink.addEventListener('mouseenter', () => swap(megaLink.dataset.megaImage));
            megaLink.addEventListener('focus', () => swap(megaLink.dataset.megaImage));
          });
          item.addEventListener('mouseleave', () => {
            if (image.dataset.current && image.dataset.current !== original.src) swap(original.src, original.srcset);
          });
        }
      });
    }
  }
  define('sticky-header', StickyHeader);

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = $$('.announcement__item', this);
      if (this.items.length < 2 || reducedMotion.matches) return;
      this.index = 0;
      const interval = Number(this.dataset.interval) || 4000;
      this.timer = setInterval(() => this.next(), interval);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    next() {
      this.items[this.index].classList.remove('is-active');
      this.items[this.index].setAttribute('aria-hidden', 'true');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].classList.add('is-active');
      this.items[this.index].removeAttribute('aria-hidden');
    }
  }
  define('announcement-bar', AnnouncementBar);

  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      const select = $('[data-autosubmit]', this);
      if (select) select.addEventListener('change', () => select.form.submit());
    }
  }
  define('localization-form', LocalizationForm);

  /* ------------------------------------------------------------ slideshow */
  class HeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = $$('[data-slide]', this);
      this.tabs = $$('[data-slide-tab]', this);
      this.index = 0;
      this.interval = Number(this.dataset.interval) || 6000;
      this.style.setProperty('--hero-interval', `${this.interval}ms`);
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1 && !reducedMotion.matches;
      if (!this.autoplay) this.dataset.autoplay = 'false';
      this.userPaused = false;

      this.tabs.forEach((tab) => tab.addEventListener('click', () => this.goTo(Number(tab.dataset.slideTab), true)));
      const prev = $('[data-slide-prev]', this);
      const next = $('[data-slide-next]', this);
      const pause = $('[data-slide-pause]', this);
      if (prev) prev.addEventListener('click', () => this.goTo(this.index - 1, true));
      if (next) next.addEventListener('click', () => this.goTo(this.index + 1, true));
      if (pause) {
        if (!this.autoplay) pause.hidden = true;
        pause.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          pause.setAttribute('aria-pressed', String(this.userPaused));
          this.userPaused ? this.pause() : this.play();
        });
      }

      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => !this.userPaused && this.play());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => !this.userPaused && this.play());
      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.goTo(this.index + 1, true);
        if (event.key === 'ArrowLeft') this.goTo(this.index - 1, true);
      });

      let startX = null;
      this.addEventListener('touchstart', (event) => (startX = event.touches[0].clientX), { passive: true });
      this.addEventListener(
        'touchend',
        (event) => {
          if (startX === null) return;
          const delta = event.changedTouches[0].clientX - startX;
          if (Math.abs(delta) > 48) this.goTo(this.index + (delta < 0 ? 1 : -1), true);
          startX = null;
        },
        { passive: true }
      );

      /* theme editor: show the slide whose block is selected */
      this.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('[data-slide]');
        if (slide) {
          this.userPaused = true;
          this.pause();
          this.goTo(this.slides.indexOf(slide));
        }
      });

      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.visible && !this.userPaused ? this.play() : this.pause();
      });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      if (this.observer) this.observer.disconnect();
    }

    goTo(index, manual = false) {
      const count = this.slides.length;
      const next = ((index % count) + count) % count;
      if (next === this.index) return;
      this.slides.forEach((slide, i) => {
        const active = i === next;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('inert', !active);
        active ? slide.removeAttribute('aria-hidden') : slide.setAttribute('aria-hidden', 'true');
        const video = $('video', slide);
        if (video) active ? video.play().catch(() => {}) : video.pause();
      });
      this.tabs.forEach((tab, i) => {
        tab.classList.toggle('is-active', i === next);
        i === next ? tab.setAttribute('aria-current', 'true') : tab.removeAttribute('aria-current');
      });
      this.index = next;
      if (manual) this.restart();
    }

    restart() {
      clearTimeout(this.timer);
      this.startedAt = null;
      this.remaining = this.interval;
      if (this.playing) this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      this.startedAt = Date.now();
      this.timer = setTimeout(() => {
        this.goTo(this.index + 1);
        this.remaining = this.interval;
        this.schedule();
      }, this.remaining || this.interval);
    }

    play() {
      if (!this.autoplay || this.playing || this.visible === false) return;
      this.playing = true;
      this.classList.remove('is-paused');
      this.schedule();
    }

    pause() {
      if (!this.playing) return;
      this.playing = false;
      this.classList.add('is-paused');
      clearTimeout(this.timer);
      if (this.startedAt) this.remaining = Math.max(800, (this.remaining || this.interval) - (Date.now() - this.startedAt));
    }
  }
  define('hero-slideshow', HeroSlideshow);

  class CarouselComponent extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-carousel-track]', this);
      this.prev = $('[data-carousel-prev]', this);
      this.next = $('[data-carousel-next]', this);
      if (!this.track) return;
      const move = (direction) => {
        const slide = this.track.querySelector('.carousel__slide') || this.track.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const amount = (slide ? slide.getBoundingClientRect().width + gap : this.track.clientWidth * 0.9) * direction;
        this.track.scrollBy({ left: amount, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      };
      if (this.prev) this.prev.addEventListener('click', () => move(-1));
      if (this.next) this.next.addEventListener('click', () => move(1));
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', debounce(this.update, 80), { passive: true });
      window.addEventListener('resize', debounce(this.update, 150));
      this.update();
    }

    update() {
      if (!this.prev || !this.next) return;
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      this.prev.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft >= max;
      const scrollable = max > 2;
      this.prev.parentElement.style.visibility = scrollable ? '' : 'hidden';
    }
  }
  define('carousel-component', CarouselComponent);

  /* ----------------------------------------------------------------- cart */
  const cart = {
    sectionIds() {
      const ids = new Set(['cart-drawer']);
      $$('cart-items[data-section]').forEach((el) => ids.add(el.dataset.section));
      return Array.from(ids);
    },

    /* The cart panel renders on any page; the cart page section only on its own url.
       Using the home page elsewhere keeps this working on pages that return a 404. */
    sectionsUrl() {
      return this.sectionIds().length > 1 ? window.location.pathname : routes.root || '/';
    },

    async request(url, body, isFormData = false) {
      const options = {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      };
      if (isFormData) {
        body.append('sections', this.sectionIds().join(','));
        body.append('sections_url', this.sectionsUrl());
        options.body = body;
      } else {
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify({ ...body, sections: this.sectionIds(), sections_url: this.sectionsUrl() });
      }
      const response = await fetch(url, options);
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        const error = new Error(data.description || data.message || strings.cartError || 'Cart error');
        error.data = data;
        throw error;
      }
      return data;
    },

    async add(payload, { isFormData = false, open = true } = {}) {
      const data = await this.request(routes.cartAdd || '/cart/add.js', payload, isFormData);
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      if (open) this.open();
      return data;
    },

    async change(key, quantity) {
      const data = await this.request(routes.cartChange || '/cart/change.js', { id: key, quantity });
      this.render(data.sections, data.item_count);
      return data;
    },

    async updateNote(note) {
      await fetch(routes.cartUpdate || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      });
    },

    render(sections, itemCount) {
      if (!sections) return;
      let count = itemCount;
      $$('cart-items[data-section]').forEach((element) => {
        const html = sections[element.dataset.section];
        if (!html) return;
        const fresh = $('cart-items', parseHTML(html));
        if (!fresh) return;
        element.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
        element.dataset.itemCount = fresh.dataset.itemCount;
        if (count === undefined) count = Number(fresh.dataset.itemCount);
        $$('[data-cart-live]', element).forEach((part) => {
          const replacement = $(`[data-cart-live="${part.dataset.cartLive}"]`, fresh);
          if (replacement) part.innerHTML = replacement.innerHTML;
        });
      });
      if (count === undefined) {
        const drawerHtml = sections['cart-drawer'];
        const fresh = drawerHtml && $('cart-items', parseHTML(drawerHtml));
        if (fresh) count = Number(fresh.dataset.itemCount);
      }
      if (count !== undefined && !Number.isNaN(count)) this.setCount(count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count } }));
      notifyDealerPricing();
    },

    setCount(count) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('is-empty', count === 0);
      });
    },

    open() {
      const drawer = document.getElementById('CartDrawer');
      if (theme.cartType === 'drawer' && drawer && typeof drawer.open === 'function') {
        drawer.open();
      } else if (!window.location.pathname.endsWith('/cart')) {
        window.location.href = routes.cart || '/cart';
      }
    },
  };
  theme.cart = cart;

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-line-remove]');
        if (remove) {
          event.preventDefault();
          this.update(remove.dataset.lineRemove, 0, remove);
          return;
        }
        const minus = event.target.closest('[data-quantity-minus]');
        const plus = event.target.closest('[data-quantity-plus]');
        if (minus || plus) {
          const input = $('[data-quantity-input]', (minus || plus).parentElement);
          const value = Math.max(0, (Number(input.value) || 0) + (plus ? 1 : -1));
          input.value = value;
          this.queue(input);
        }
      });
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-quantity-input]')) this.queue(event.target);
        if (event.target.matches('[data-cart-note]')) cart.updateNote(event.target.value);
      });
      this.addEventListener('submit', (event) => {
        if (event.submitter && event.submitter.name === 'checkout') notifyDealerPricing('CartCheckout', 'ClickCheckout');
      });
      /* one timer per cart line, so quick changes on two lines both go through */
      this.timers = new Map();
      this.queue = (input) => {
        const key = input.dataset.lineKey;
        clearTimeout(this.timers.get(key));
        this.timers.set(
          key,
          setTimeout(() => {
            this.timers.delete(key);
            this.update(key, Number(input.value), input);
          }, 350)
        );
      };
    }

    async update(key, quantity, source) {
      const line = source.closest('[data-cart-line]');
      const lineIndex = line ? line.dataset.lineIndex : null;
      /* the empty box leaves the cart together with the last thing that was in it */
      let caseKey = null;
      if (quantity === 0 && line && line.dataset.boxId && !line.hasAttribute('data-box-case')) {
        const group = $$('[data-cart-line]', this).filter((el) => el.dataset.boxId === line.dataset.boxId && el !== line);
        const boxCase = group.find((el) => el.hasAttribute('data-box-case'));
        if (boxCase && group.length === 1) caseKey = boxCase.dataset.lineKey;
      }
      this.classList.add('is-updating');
      try {
        await cart.change(key, quantity);
        if (caseKey) await cart.change(caseKey, 0);
        /* keep keyboard users in place after the list re-rendered */
        const sameLine = lineIndex && $(`[data-line-index="${lineIndex}"] [data-quantity-input]`, this);
        if (sameLine && this.contains(document.activeElement) === false) sameLine.focus({ preventScroll: true });
      } catch (error) {
        const errorEl = line && $('[data-line-error]', line);
        if (errorEl) {
          errorEl.textContent = error.message;
          errorEl.hidden = false;
        }
        const input = line && $('[data-quantity-input]', line);
        if (input) input.value = input.defaultValue;
      } finally {
        this.classList.remove('is-updating');
      }
    }
  }
  define('cart-items', CartItems);

  /* On the cart page and in the drawer the dealer app needs a nudge once its markup is on screen. */
  document.addEventListener('drawer:open', (event) => {
    if (event.target.id === 'CartDrawer') notifyDealerPricing();
  });

  /* ---------------------------------------------------------- product card */
  class ProductCard extends HTMLElement {
    connectedCallback() {
      this.isStatic = this.hasAttribute('data-static');
      this.image = $('.card__image--main', this);
      this.hoverImage = $('.card__image--hover', this);
      this.links = $$('[data-card-link]', this);
      this.price = $('[data-card-price]', this);
      this.variation = $('[data-card-variation]', this);
      this.variationSr = $('[data-card-variation-sr]', this);
      this.quick = $('[data-card-quick]', this);
      this.panel = $('[data-quick-panel]', this);
      this.swatches = $$('[data-swatch]', this);
      this.current = this.swatches.find((swatch) => swatch.classList.contains('is-current')) || null;
      this.sizeCache = new Map();
      /* leaving the card shows its own color again */ if (!this.isStatic && this.current) this.addEventListener('mouseleave', () => { if (window.matchMedia('(hover: hover)').matches) this.preview(this.current); });
      if (this.current) {
        this.current.classList.add('is-active');
        if (this.panel) this.sizeCache.set(this.current.dataset.productId, this.panel.innerHTML);
      }
    this.nameEl = $('[data-card-name]', this);
      if (!this.isStatic) {
        this.swatches.forEach((swatch) => {
          swatch.addEventListener('mouseenter', () => this.preview(swatch));
          swatch.addEventListener('focus', () => this.preview(swatch));
          swatch.addEventListener('click', (event) => {
            /* touch: first tap previews the color, second tap opens the product */
            if (window.matchMedia('(hover: none)').matches && this.tapped !== (this.tapped = swatch)) {
              event.preventDefault();
              this.preview(swatch);
            }
          });
        });
      }

      const toggle = $('[data-quick-toggle]', this);
      if (toggle) {
        toggle.addEventListener('click', () => {
          const open = this.quick.classList.toggle('is-open');
          toggle.setAttribute('aria-expanded', String(open));
        });
      }

      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-quick-variant]');
        if (button && !button.disabled) this.quickAdd(button);
      });
    }

    preview(swatch) {
      if (swatch === this.active) return;
      this.active = swatch;
      const data = swatch.dataset;
      this.swatches.forEach((item) => item.classList.toggle('is-active', item === swatch));
      this.dataset.productId = data.productId;

      if (this.image && data.image) {
        this.image.removeAttribute('srcset');
        this.image.src = data.image;
        try {
          /* same photo in a few widths, by changing the width in the image url */
          const source = new URL(data.image, window.location.href);
          this.image.srcset = [300, 500, 800]
            .map((w) => {
              source.searchParams.set('width', w);
              return `${source.href} ${w}w`;
            })
            .join(', ');
        } catch (error) {
          /* keep the single image */
        }
      }
      if (this.hoverImage) {
        this.hoverImage.removeAttribute('srcset');
        if (data.imageHover) {
          this.hoverImage.src = data.imageHover;
          this.hoverImage.hidden = false;
        } else {
          this.hoverImage.hidden = true;
        }
      }
      this.links.forEach((link) => (link.href = data.url));
      if (this.variation) this.variation.textContent = data.variation;
      if (this.variationSr) this.variationSr.textContent = ` ${data.variation}`;
      if (this.price && data.price) this.price.innerHTML = data.price;
      if (this.nameEl && data.name) this.nameEl.textContent = data.name; this.classList.toggle('card--soldout', data.available === 'false');
      this.loadSizes(data);
    }

    async loadSizes(data) {
      if (!this.panel) return;
      const id = data.productId;
      if (this.sizeCache.has(id)) {
        this.panel.innerHTML = this.sizeCache.get(id);
        return;
      }
      this.quick.classList.add('is-loading');
      try {
        const url = new URL(data.url, window.location.origin);
        url.searchParams.set('section_id', 'card-sizes');
        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(String(response.status));
        const doc = parseHTML(await response.text());
        const section = $('.shopify-section', doc) || doc.body;
        this.sizeCache.set(id, section.innerHTML);
        if (this.dataset.productId === id) this.panel.innerHTML = section.innerHTML;
      } catch (error) {
        this.panel.innerHTML = `<a class="card__quick-link" href="${data.url}">${strings.addToCart || 'View'}</a>`;
      } finally {
        this.quick.classList.remove('is-loading');
      }
    }

    async quickAdd(button) {
      const original = button.innerHTML;
      button.disabled = true;
      /* keeps the normal look of the size while it is being added */
      button.classList.add('is-adding');
      try {
        await cart.add({ items: [{ id: Number(button.dataset.quickVariant), quantity: 1 }] });
        if (this.quick) this.quick.classList.remove('is-open');
      } catch (error) {
        button.textContent = '!';
        button.title = error.message;
        setTimeout(() => {
          button.innerHTML = original;
          button.title = '';
        }, 2000);
      } finally {
        button.disabled = false;
        button.classList.remove('is-adding');
      }
    }
  }
  define('product-card', ProductCard);

  /* --------------------------------------------------------- product page */
  class ColorPicker extends HTMLElement {
    connectedCallback() {
      const label = $('[data-color-label]', this);
      if (!label) return;
      $$('[data-sibling]', this).forEach((option) => {
        const show = () => (label.textContent = option.dataset.variation);
        const reset = () => (label.textContent = label.dataset.default);
        option.addEventListener('mouseenter', show);
        option.addEventListener('focus', show);
        option.addEventListener('mouseleave', reset);
        option.addEventListener('blur', reset);
      });
    }
  }
  define('color-picker', ColorPicker);

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      this.button = $('[data-add-button]', this);
      this.buttonLabel = $('[data-add-label]', this);
      this.error = $('[data-form-error]', this);
      this.stockNote = $('.product__stock', this);
      this.priceEl = $('[data-product-price]', this.closest('[data-product-page]') || document);
      this.variantInputs = $$('[data-variant-input]', this);
      this.optionInputs = $$('[data-option-input]', this);
      this.hiddenId = $('[data-variant-id]', this);
      const json = $('[data-variants-json]', this);
      this.variants = json ? JSON.parse(json.textContent) : null;

      this.variantInputs.forEach((input) => input.addEventListener('change', () => this.onVariantInput(input)));
      this.optionInputs.forEach((input) => input.addEventListener('change', () => this.onOptionInput()));
      if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));

      /* gift card: the recipient fields only count while "send as a gift" is open */
      const gift = $('[data-gift-recipient]', this);
      if (gift) {
        const flag = $('[data-gift-flag]', gift);
        const fields = $$('[data-gift-field]', gift);
        const offset = $('[data-gift-offset]', gift);
        const sync = () => {
          flag.checked = gift.open;
          fields.forEach((field) => {
            field.disabled = !gift.open;
            if (field.hasAttribute('data-gift-required')) field.required = gift.open;
          });
          if (offset) offset.value = new Date().getTimezoneOffset();
        };
        gift.addEventListener('toggle', sync);
        sync();
      }

      const checked = this.variantInputs.find((input) => input.checked);
      if (checked) this.onVariantInput(checked, false); else if (this.stockNote && this.variantInputs.length) { const any = this.variantInputs.some((i) => i.dataset.available !== 'false'); this.stockNote.dataset.level = any ? 'in' : 'out'; this.stockNote.textContent = any ? (this.stockNote.dataset.textIn || 'In stock') : (this.stockNote.dataset.textOut || 'Sold out'); this.stockNote.hidden = false; }
    }

    get selectedId() {
      if (this.variantInputs.length) {
        const checked = this.variantInputs.find((input) => input.checked);
        return checked ? checked.value : null;
      }
      if (this.hiddenId) return this.hiddenId.value || null;
      const plain = this.form && this.form.querySelector('input[name="id"]');
      return plain ? plain.value : null;
    }

    onVariantInput(input, updateUrl = true) {
      const data = input.dataset;
      const label = $('[data-variant-label]', this);
      if (label) label.textContent = data.title;
      if (this.stockNote) {
        const level = data.available === 'false' ? 'out' : (data.stockNote ? 'low' : 'in'); this.stockNote.dataset.level = level; this.stockNote.textContent = level === 'out' ? (this.stockNote.dataset.textOut || 'Sold out') : (data.stockNote || this.stockNote.dataset.textIn || 'In stock'); this.stockNote.hidden = false;
      }
      this.setPrice(data.price, data.compare, input.value);
      this.setState(true, data.available !== 'false');
      this.clearError();
      if (updateUrl) this.setUrl(input.value);
    }

    onOptionInput() {
      const fieldsets = $$('[data-option-fieldset]', this);
      const chosen = fieldsets.map((fieldset) => {
        const input = $('[data-option-input]:checked', fieldset);
        const label = $('[data-option-label]', fieldset);
        if (input && label) label.textContent = input.value;
        return input ? input.value : null;
      });
      if (chosen.includes(null)) return;
      const variant = (this.variants || []).find((item) => item.options.every((value, index) => value === chosen[index]));
      this.hiddenId.value = variant ? variant.id : '';
      if (!variant) {
        this.setState(false, false, strings.unavailable);
        return;
      }
      const compare = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '';
      this.setPrice(formatMoney(variant.price), compare, variant.id);
      this.setState(true, variant.available);
      this.clearError();
      this.setUrl(variant.id);
    }

    setPrice(price, compare, variantId) {
      const targets = [this.priceEl, $('[data-sticky-price]')].filter(Boolean);
      targets.forEach((target) => {
        const priceEl = $('.price', target);
        if (!priceEl) return;
        if (priceEl.hasAttribute('bss-b2b-variant-id')) priceEl.setAttribute('bss-b2b-variant-id', variantId);
        /* dealer prices are written by the BSS app, leave them alone */
        if (theme.isDealer || !price) return;
        priceEl.classList.toggle('price--sale', Boolean(compare));
        priceEl.innerHTML = compare
          ? `<span class="price__current">${price}</span> <s class="price__compare">${compare}</s>`
          : `<span class="price__current">${price}</span>`;
      });
    }

    setState(selected, available, text) {
      if (!this.button) return;
      const label = text || (!selected ? this.button.dataset.labelSelect : available ? this.button.dataset.labelAdd : this.button.dataset.labelSoldout);
      this.buttonLabel.textContent = label;
      this.button.disabled = selected && !available;
      document.dispatchEvent(new CustomEvent('product:state', { detail: { label, disabled: this.button.disabled, selected } }));
    }

    setUrl(variantId) {
      /* "Make it a gift box" takes the chosen size along */
      const boxLink = $('[data-giftbox-link]', this);
      if (boxLink && variantId) boxLink.href = `${boxLink.dataset.base}&box_add=${variantId}`;
      if (!this.dataset.section || !window.history.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    clearError() {
      if (this.error) this.error.hidden = true;
      $$('.product__option.is-missing', this).forEach((el) => el.classList.remove('is-missing'));
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.button && this.button.disabled) return;
      if (!this.selectedId) {
        const fieldset = $('[data-variant-fieldset], [data-option-fieldset]', this);
        if (fieldset) {
          fieldset.classList.add('is-missing');
          fieldset.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
          const first = $('input:not([disabled])', fieldset);
          if (first) first.focus({ preventScroll: true });
        }
        this.showError(this.button ? this.button.dataset.labelSelect : '');
        return;
      }
      const invalid = $$('[data-gift-field]', this).find((field) => !field.disabled && !field.checkValidity());
      if (invalid) {
        invalid.reportValidity();
        return;
      }
      this.clearError();
      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-busy', 'true');
      const formData = new FormData(this.form);
      cart
        .add(formData, { isFormData: true })
        .then(() => {
          this.buttonLabel.textContent = strings.added || this.button.dataset.labelAdd;
          setTimeout(() => (this.buttonLabel.textContent = this.button.dataset.labelAdd), 2000);
        })
        .catch((error) => this.showError(error.message))
        .finally(() => {
          this.button.classList.remove('is-loading');
          this.button.removeAttribute('aria-busy');
        });
    }
  }
  define('product-form', ProductForm);

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.form);
      this.button = $('[data-sticky-button]', this);
      this.label = $('[data-sticky-label]', this);
      const target = this.form && $('[data-add-button]', this.form);
      if (!target) return;

      this.observer = new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', passed);
        this.setAttribute('aria-hidden', String(!passed));
        this.button.tabIndex = passed ? 0 : -1;
      });
      this.observer.observe(target);

      this.button.addEventListener('click', () => {
        if (target.disabled) return;
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(target);
        else target.click();
      });
      document.addEventListener('product:state', (event) => {
        this.label.textContent = event.detail.label;
        this.button.disabled = event.detail.disabled;
      });
      /* the product form may have announced its state before this element existed */
      this.button.disabled = target.disabled;
      const mainLabel = $('[data-add-label]', target);
      if (mainLabel) this.label.textContent = mainLabel.textContent.trim();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }
  define('sticky-atc', StickyAtc);

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-gallery-track]', this);
      if (!this.track) return;
      this.items = $$('[data-gallery-item]', this);
      this.current = $('[data-gallery-current]', this);
      this.thumbs = $$('[data-gallery-thumb]', this);
      this.prev = $('[data-gallery-prev]', this);
      this.next = $('[data-gallery-next]', this);
      this.lightbox = $('[data-gallery-lightbox]', this);
      this.index = 0;

      const behavior = () => (reducedMotion.matches ? 'auto' : 'smooth');
      const goTo = (index) => {
        const item = this.items[Math.max(0, Math.min(this.items.length - 1, index))];
        if (item) this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: behavior() });
      };
      if (this.prev) this.prev.addEventListener('click', () => goTo(this.index - 1));
      if (this.next) this.next.addEventListener('click', () => goTo(this.index + 1));
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => goTo(Number(thumb.dataset.galleryThumb))));

      this.track.addEventListener(
        'scroll',
        debounce(() => {
          /* step = distance between two slides, so it also works when two photos show side by side */
          const step = (this.items[1] ? this.items[1].offsetLeft - this.items[0].offsetLeft : this.track.clientWidth) || 1;
          const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
          this.setIndex(atEnd ? this.items.length - 1 : Math.round(this.track.scrollLeft / step));
        }, 60),
        { passive: true }
      );
      this.setIndex(0);

      if (this.lightbox) {
        $$('[data-gallery-zoom]', this).forEach((button) => button.addEventListener('click', () => this.openLightbox(Number(button.dataset.galleryZoom))));
        this.lightbox.addEventListener('click', (event) => {
          if (event.target.closest('[data-lightbox-close]') || event.target.closest('[data-lightbox-scroller]')) this.lightbox.close();
        });
      }
    }

    setIndex(index) {
      this.index = Math.max(0, Math.min(this.items.length - 1, index));
      if (this.current) this.current.textContent = this.index + 1;
      this.thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === this.index));
      if (this.prev) this.prev.disabled = this.index === 0;
      if (this.next) this.next.disabled = this.index === this.items.length - 1;
    }

    openLightbox(index) {
      if (typeof this.lightbox.showModal !== 'function') return;
      this.lightbox.showModal();
      const image = $(`[data-lightbox-image="${index}"]`, this.lightbox);
      if (image) image.scrollIntoView({ block: 'start' });
    }
  }
  define('product-gallery', ProductGallery);

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.loaded) return;
      const load = () => {
        this.loaded = true;
        fetch(this.dataset.url)
          .then((response) => (response.ok ? response.text() : ''))
          .then((html) => {
            const doc = parseHTML(html);
            const fresh = $('product-recommendations', doc) || $('.shopify-section', doc) || doc.body;
            if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
          })
          .catch(() => {});
      };
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (!entry.isIntersecting) return;
            observer.disconnect();
            load();
          },
          { rootMargin: '400px 0px' }
        );
        observer.observe(this);
      } else {
        load();
      }
    }
  }
  define('product-recommendations', ProductRecommendations);

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      let handles = [];
      try {
        handles = JSON.parse(localStorage.getItem('ew:recently-viewed') || '[]');
      } catch (error) {
        handles = [];
      }
      handles = handles.filter((handle) => handle !== this.dataset.exclude).slice(0, Number(this.dataset.limit) || 4);
      if (!handles.length) return;
      const root = (this.dataset.root || '/').replace(/\/$/, '');
      const list = $('[data-recently-results]', this);

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          Promise.all(
            handles.map((handle) =>
              fetch(`${root}/products/${handle}?section_id=product-card-fragment`)
                .then((response) => (response.ok ? response.text() : ''))
                .catch(() => '')
            )
          ).then((results) => {
            results.forEach((html) => {
              if (!html) return;
              const card = $('product-card', parseHTML(html));
              if (!card) return;
              const li = document.createElement('li');
              li.appendChild(document.importNode(card, true));
              list.appendChild(li);
            });
            if (list.children.length) this.hidden = false;
          });
        },
        { rootMargin: '600px 0px' }
      );
      /* hidden elements never intersect, so watch the section wrapper instead */
      observer.observe(this.closest('.shopify-section') || this.parentElement);
    }
  }
  define('recently-viewed', RecentlyViewed);

  /* ----------------------------------------------------- search, filters */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.results = $('[data-predictive-results]', this);
      if (!this.input || !this.results) return;
      this.cache = new Map();
      this.input.addEventListener(
        'input',
        debounce(() => this.search(), 220)
      );
    }

    async search() {
      const term = this.input.value.trim();
      if (term.length < 2) {
        this.results.innerHTML = '';
        this.input.setAttribute('aria-expanded', 'false');
        return;
      }
      if (this.cache.has(term)) {
        this.show(this.cache.get(term));
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const url = `${routes.predictiveSearch || '/search/suggest'}?q=${encodeURIComponent(term)}&section_id=predictive-search&resources[type]=product,collection,page&resources[limit]=6&resources[limit_scope]=each&resources[options][fields]=title,product_type,variants.title,tag`;
      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const doc = parseHTML(await response.text());
        const fresh = $('#predictive-search-results', doc);
        const html = fresh ? fresh.outerHTML : '';
        this.cache.set(term, html);
        if (this.input.value.trim() === term) this.show(html);
      } catch (error) {
        if (error.name !== 'AbortError') this.results.innerHTML = '';
      }
    }

    show(html) {
      this.results.innerHTML = html;
      this.input.setAttribute('aria-expanded', String(Boolean(html)));
    }
  }
  define('predictive-search', PredictiveSearch);

  class CollectionPage extends HTMLElement {
    connectedCallback() {
      this.results = $('[data-collection-results]', this);
      this.form = $('[data-facet-form]', this);
      this.applyView();

      this.addEventListener('click', (event) => {
        const view = event.target.closest('button[data-grid-view]');
        if (view) {
          try {
            localStorage.setItem('ew:grid-view', view.dataset.gridView);
          } catch (error) {
            /* private mode */
          }
          this.applyView(view.dataset.gridView);
          return;
        }
        const remove = event.target.closest('[data-facet-remove], [data-facet-reset]');
        if (remove && this.results) {
          event.preventDefault();
          this.load(remove.href);
          const drawer = remove.closest('drawer-panel');
          if (drawer) drawer.close();
        }
      });

      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.submit();
        });
        this.form.addEventListener(
          'change',
          debounce(() => this.submit(), 500)
        );
      }

      this.lastLocation = window.location.pathname + window.location.search;
      this.onPopState = () => {
        /* in-page links (#...) also fire popstate: nothing to reload then */
        const current = window.location.pathname + window.location.search;
        if (current === this.lastLocation) return;
        this.lastLocation = current;
        this.load(window.location.href, false);
      };
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
    }

    applyView(value) {
      let view = value;
      if (!view) {
        try {
          view = localStorage.getItem('ew:grid-view');
        } catch (error) {
          view = null;
        }
      }
      if (!view) return;
      this.dataset.gridView = view;
      $$('[data-grid-view]', this).forEach((button) => {
        if (button !== this) button.setAttribute('aria-pressed', String(button.dataset.gridView === view));
      });
    }

    submit() {
      const params = new URLSearchParams();
      new FormData(this.form).forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
      const query = params.toString();
      this.load(`${window.location.pathname}${query ? `?${query}` : ''}`);
    }

    async load(url, push = true) {
      if (!this.results) {
        window.location.href = url;
        return;
      }
      this.results.classList.add('is-loading');
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(String(response.status));
        const doc = parseHTML(await response.text());
        const freshResults = $('[data-collection-results]', doc);
        const freshForm = $('[data-facet-form]', doc);
        if (!freshResults) throw new Error('missing results');
        this.results.innerHTML = freshResults.innerHTML;
        /* refresh counts and checked states, but keep the drawer itself open */
        if (freshForm && this.form) {
          const body = $('.drawer__body', this.form);
          const freshBody = $('.drawer__body', freshForm);
          const openGroups = $$('details', body).map((details) => details.open);
          if (body && freshBody) {
            body.innerHTML = freshBody.innerHTML;
            $$('details', body).forEach((details, index) => {
              if (openGroups[index] !== undefined) details.open = openGroups[index];
            });
          }
        }
        this.applyView();
        if (push) window.history.pushState({}, '', url);
        this.lastLocation = window.location.pathname + window.location.search;
        notifyDealerPricing();
      } catch (error) {
        window.location.href = url;
      } finally {
        this.results.classList.remove('is-loading');
      }
    }
  }
  define('collection-page', CollectionPage);

  class LoadMore extends HTMLElement {
    connectedCallback() {
      this.link = $('[data-load-more]', this);
      if (!this.link) return;
      this.link.addEventListener('click', (event) => {
        event.preventDefault();
        this.load();
      });
      if (this.dataset.mode === 'infinite' && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) this.load();
          },
          { rootMargin: '600px 0px' }
        );
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    async load() {
      if (this.loading || !this.link) return;
      this.loading = true;
      this.classList.add('is-loading');
      const grid = this.parentElement.querySelector('[data-product-grid]');
      try {
        const response = await fetch(this.link.href);
        if (!response.ok) throw new Error(String(response.status));
        const doc = parseHTML(await response.text());
        const freshGrid = $('[data-product-grid]', doc);
        const freshMore = $('[data-load-more]', doc);
        if (freshGrid && grid) {
          Array.from(freshGrid.children).forEach((child) => grid.appendChild(document.importNode(child, true)));
        }
        if (freshMore) {
          this.link.href = freshMore.href;
        } else {
          if (this.observer) this.observer.disconnect();
          this.remove();
        }
        notifyDealerPricing();
      } catch (error) {
        window.location.href = this.link.href;
      } finally {
        this.loading = false;
        this.classList.remove('is-loading');
      }
    }
  }
  define('load-more', LoadMore);

  /* ------------------------------------------------------ gift box builder */
  class GiftboxBuilder extends HTMLElement {
    connectedCallback() {
      try {
        this.combos = JSON.parse(this.dataset.combos || '[]');
      } catch (error) {
        this.combos = [];
      }
      this.minItems = Number(this.dataset.min) || 1;
      this.boxVariant = this.dataset.boxVariant;
      this.boxPrice = this.boxVariant ? Number(this.dataset.boxPrice) || 0 : 0;
      this.items = [];
      this.cats = $$('[data-cat]', this);
      this.chips = $$('[data-cat-chip]', this);
      this.list = $('[data-box-items]', this);
      this.slots = $('[data-box-slots]', this);
      this.hint = $('[data-box-hint]', this);
      this.total = $('[data-box-total]', this);
      this.submit = $('[data-box-submit]', this);
      this.error = $('[data-box-error]', this);
      this.message = $('[data-box-message]', this);
      this.counter = $('[data-box-count]', this);

      this.addEventListener('click', (event) => {
        const add = event.target.closest('[data-box-add]');
        if (add && !add.disabled) {
          this.add(add);
          return;
        }
        const remove = event.target.closest('[data-box-remove]');
        if (remove) {
          this.remove(Number(remove.dataset.boxRemove));
          return;
        }
        const toggle = event.target.closest('[data-box-toggle]');
        if (toggle) {
          const open = toggle.getAttribute('aria-expanded') !== 'true';
          toggle.setAttribute('aria-expanded', String(open));
          this.classList.toggle('is-summary-open', open);
        }
      });
      if (this.submit) this.submit.addEventListener('click', () => this.addToCart());
      this.update();
      this.prefill();
    }

    /* Coming from a product page: put that product in the box, or at least bring it into view. */
    prefill() {
      const params = new URLSearchParams(window.location.search);
      const variantId = params.get('box_add');
      const handle = params.get('box_product');
      let target = null;
      if (variantId) {
        const button = $$('[data-box-add]', this).find((el) => el.dataset.variantId === variantId && !el.disabled);
        if (button) {
          this.add(button);
          target = button.closest('[data-box-product]');
        }
      }
      if (!target && handle) target = $$('[data-box-product]', this).find((el) => el.dataset.handle === handle);
      if (target) {
        target.classList.add('is-arrived');
        requestAnimationFrame(() => target.scrollIntoView({ block: 'center' }));
      }
    }

    /* how many items of each category a selection holds */
    counts(items = this.items) {
      const counts = this.cats.map(() => 0);
      items.forEach((item) => {
        counts[item.cat] += 1;
      });
      return counts;
    }

    /* a selection is fine when it stays within at least one combination */
    fits(counts) {
      const total = counts.reduce((sum, n) => sum + n, 0);
      if (!this.combos.length) return total <= 3;
      return this.combos.some((combo) => total <= combo.total && counts.every((n, index) => n <= (combo.max[index] || 0)));
    }

    canAdd(cat) {
      const counts = this.counts();
      counts[cat] += 1;
      return this.fits(counts);
    }

    /* the most one box can ever hold of a category */
    catMax(cat) {
      if (!this.combos.length) return 3;
      return Math.max(0, ...this.combos.map((combo) => Math.min(combo.max[cat] || 0, combo.total)));
    }

    /* how many more items could still go in */
    remaining() {
      const counts = this.counts();
      const total = counts.reduce((sum, n) => sum + n, 0);
      if (!this.combos.length) return Math.max(0, 3 - total);
      let best = 0;
      this.combos.forEach((combo) => {
        if (total > combo.total || !counts.every((n, index) => n <= (combo.max[index] || 0))) return;
        const room = counts.reduce((sum, n, index) => sum + ((combo.max[index] || 0) - n), 0);
        best = Math.max(best, Math.min(combo.total - total, room));
      });
      return best;
    }

    add(button) {
      const cat = Number(button.closest('[data-cat]').dataset.cat);
      if (!this.canAdd(cat)) {
        /* a kind that goes in once (a beanie, a T-shirt) swaps to the new choice */
        const without = this.items.filter((item) => item.cat !== cat);
        const swapped = this.counts(without);
        swapped[cat] += 1;
        if (this.catMax(cat) === 1 && without.length < this.items.length && this.fits(swapped)) {
          this.items = without;
        } else {
          this.showError(this.counts()[cat] > 0 && this.counts()[cat] >= this.catMax(cat) ? this.dataset.stringCatFull : this.dataset.stringNoFit);
          return;
        }
      }
      this.clearError();
      const data = button.dataset;
      this.items.push({
        uid: Date.now() + Math.random(),
        cat,
        variantId: Number(data.variantId),
        price: Number(data.price),
        title: data.title,
        variantTitle: data.variantTitle,
        image: data.image,
      });
      this.update();
    }

    remove(uid) {
      this.items = this.items.filter((item) => item.uid !== uid);
      this.clearError();
      this.update();
    }

    update() {
      const counts = this.counts();
      const room = this.remaining();

      /* contents */
      this.list.innerHTML = '';
      this.items.forEach((item) => {
        const li = document.createElement('li');
        li.className = 'giftbox__item';
        const image = document.createElement('img');
        image.alt = '';
        if (item.image) image.src = item.image;
        const text = document.createElement('div');
        const title = document.createElement('p');
        title.className = 'giftbox__item-title';
        title.textContent = item.title;
        const meta = document.createElement('p');
        meta.className = 'giftbox__item-meta';
        meta.textContent = [item.variantTitle, formatMoney(item.price)].filter(Boolean).join(' · ');
        text.append(title, meta);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'giftbox__item-remove';
        button.dataset.boxRemove = item.uid;
        button.setAttribute('aria-label', `${this.dataset.stringRemove} ${item.title}`);
        button.innerHTML = '<svg class="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M5 5l14 14M19 5 5 19"/></svg>';
        li.append(image, text, button);
        this.list.appendChild(li);
      });
      if (this.counter) this.counter.textContent = this.items.length;

      /* the box drawn as places: filled ones show the product, open ones are dashed */
      if (this.slots) {
        this.slots.innerHTML = '';
        this.items.forEach((item) => {
          const slot = document.createElement('span');
          slot.className = 'giftbox__slot is-filled';
          if (item.image) slot.style.backgroundImage = `url("${item.image}")`;
          this.slots.appendChild(slot);
        });
        for (let i = 0; i < room; i += 1) {
          const slot = document.createElement('span');
          slot.className = 'giftbox__slot';
          slot.title = this.dataset.stringFreeSlot || '';
          this.slots.appendChild(slot);
        }
      }

      /* per category: what is chosen, and whether more of it still fits */
      const addable = [];
      const boxFull = this.items.length > 0 && !this.cats.some((section, cat) => this.canAdd(cat));
      this.cats.forEach((section, cat) => {
        const open = this.canAdd(cat);
        const max = this.catMax(cat);
        const swappable = !open && max === 1 && counts[cat] === 1;
        if (open) addable.push(section.dataset.title);
        section.classList.toggle('is-locked', !open && !swappable);
        section.classList.toggle('has-items', counts[cat] > 0);

        const rule = $('[data-cat-rule]', section);
        if (rule) rule.textContent = max > 0 ? (this.dataset.stringUpTo || '').replace('[max]', max) : '';
        const note = $('[data-cat-note]', section);
        if (note) {
          let text = '';
          if (!open && !swappable) {
            if (boxFull) text = this.dataset.stringFull;
            else if (counts[cat] >= max && counts[cat] > 0) text = this.dataset.stringCatFull;
            else text = this.dataset.stringNoFit;
          }
          note.textContent = text;
          note.hidden = !text;
        }

        const chosen = this.items.filter((item) => item.cat === cat).map((item) => item.variantId);
        $$('[data-box-product]', section).forEach((product) => {
          let selected = false;
          $$('[data-box-add]', product).forEach((button) => {
            const amount = chosen.filter((id) => id === Number(button.dataset.variantId)).length;
            button.setAttribute('aria-pressed', String(amount > 0));
            const badge = $('[data-box-qty]', button);
            if (badge) {
              badge.textContent = amount > 1 ? `×${amount}` : '';
              badge.hidden = amount < 2;
            }
            if (amount > 0) selected = true;
          });
          product.classList.toggle('is-selected', selected);
        });

        const chip = this.chips[cat];
        if (chip) {
          chip.classList.toggle('is-locked', !open && !swappable);
          chip.classList.toggle('has-items', counts[cat] > 0);
          const chipCount = $('[data-cat-chip-count]', chip);
          if (chipCount) {
            chipCount.textContent = counts[cat];
            chipCount.hidden = counts[cat] === 0;
          }
        }
      });

      /* what can still be added */
      if (this.hint) {
        if (!this.items.length) this.hint.textContent = this.dataset.stringEmpty;
        else if (!addable.length) this.hint.textContent = this.dataset.stringFull;
        else this.hint.textContent = (this.dataset.stringCanAdd || '').replace('[items]', addable.join(', '));
      }
      this.classList.toggle('is-full', this.items.length > 0 && !addable.length);

      const total = this.boxPrice + this.items.reduce((sum, item) => sum + item.price, 0);
      this.total.textContent = formatMoney(total);
      if (this.submit) this.submit.disabled = this.items.length < this.minItems || !this.fits(counts);
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    clearError() {
      if (this.error) this.error.hidden = true;
    }

    async addToCart() {
      if (this.items.length < this.minItems) return;
      const code = Math.random().toString(36).slice(2, 6).toUpperCase();
      const boxId = `box-${Date.now().toString(36)}`;
      const note = this.message && this.message.value.trim();

      /* the same sock twice becomes one line with quantity 2 */
      const lines = [];
      this.items.forEach((item) => {
        const line = lines.find((entry) => entry.id === item.variantId);
        if (line) line.quantity += 1;
        else lines.push({ id: item.variantId, quantity: 1, properties: { _giftbox: boxId, 'Gift box': code } });
      });
      if (this.boxVariant) {
        lines.push({ id: Number(this.boxVariant), quantity: 1, properties: { _giftbox: boxId, _giftbox_case: '1', 'Gift box': code } });
      }
      if (note) lines[lines.length - 1].properties['Gift message'] = note;

      this.submit.classList.add('is-loading');
      this.clearError();
      try {
        await cart.add({ items: lines });
        this.items = [];
        if (this.message) this.message.value = '';
        this.update();
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.submit.classList.remove('is-loading');
      }
    }
  }
  define('giftbox-builder', GiftboxBuilder);

  /* -------------------------------------------------------------- account */
  class AddressBook extends HTMLElement {
    connectedCallback() {
      $$('[data-country-select]', this).forEach((country) => {
        const field = country.closest('.address-fields');
        const province = $('[data-province-select]', field);
        const provinceField = $('[data-province-field]', field);
        const fill = () => {
          const option = country.options[country.selectedIndex];
          let provinces = [];
          try {
            provinces = JSON.parse((option && option.dataset.provinces) || '[]');
          } catch (error) {
            provinces = [];
          }
          province.innerHTML = '';
          provinces.forEach(([value, label]) => {
            const item = document.createElement('option');
            item.value = value;
            item.textContent = label;
            if (value === province.dataset.default) item.selected = true;
            province.appendChild(item);
          });
          provinceField.hidden = provinces.length === 0;
        };
        if (country.dataset.default) {
          const match = Array.from(country.options).find((option) => option.value === country.dataset.default || option.textContent === country.dataset.default);
          if (match) match.selected = true;
        }
        country.addEventListener('change', fill);
        fill();
      });

      $$('form[data-confirm]', this).forEach((form) => {
        form.addEventListener('submit', (event) => {
          if (form.dataset.confirmed === 'true') return;
          event.preventDefault();
          const button = $('button[type="submit"]', form);
          /* two-step delete without a browser dialog */
          button.textContent = form.dataset.confirm;
          form.dataset.confirmed = 'true';
          setTimeout(() => {
            form.dataset.confirmed = 'false';
          }, 4000);
        });
      });
    }
  }
  define('address-book', AddressBook);

  /* ----------------------------------------- page-to-page smoothness helpers */
  const SCROLL_KEY = 'ew:keep-scroll';

  /* Switching color or stepping to the next product is a normal page load (apps keep working),
     but the page keeps its scroll position so it feels like the same page. */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-sibling], a[data-keep-scroll]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || link.target === '_blank') return;
    try {
      const target = new URL(link.href, window.location.origin);
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ path: target.pathname, y: window.scrollY, time: Date.now() }));
    } catch (error) {
      /* storage unavailable */
    }
  });

  const restoreScroll = () => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || 'null');
      sessionStorage.removeItem(SCROLL_KEY);
      if (!saved || saved.path !== window.location.pathname || Date.now() - saved.time > 15000) return;
      const maxY = document.documentElement.scrollHeight - window.innerHeight;
      window.scrollTo({ top: Math.min(saved.y, Math.max(0, maxY)), behavior: 'auto' });
    } catch (error) {
      /* storage unavailable */
    }
  };

  /* Browsers without speculation rules: preload a page when a link is hovered or touched. */
  const prefetched = new Set();
  const prefetch = (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a[href]');
    if (!link || link.target === '_blank' || link.hasAttribute('download')) return;
    let url;
    try {
      url = new URL(link.href);
    } catch (error) {
      return;
    }
    if (url.origin !== window.location.origin) return;
    if (/^(\/[a-z]{2}(-[a-z]{2})?)?\/(cart|account|checkout|tools|apps)(\/|$)/i.test(url.pathname) || url.pathname === window.location.pathname) return;
    if (prefetched.has(url.href)) return;
    prefetched.add(url.href);
    const hint = document.createElement('link');
    hint.rel = 'prefetch';
    hint.href = url.href;
    document.head.appendChild(hint);
  };

  const init = () => {
    restoreScroll();

    const supportsSpeculation = typeof HTMLScriptElement !== 'undefined' && HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules');
    const saveData = navigator.connection && navigator.connection.saveData;
    if (theme.prefetch && !supportsSpeculation && !saveData) {
      document.addEventListener('mouseover', debounce(prefetch, 80), { passive: true });
      document.addEventListener('touchstart', prefetch, { passive: true });
    }

    /* a short flash of the accent color on the buy button or size that was just pressed */
    document.addEventListener(
      'pointerdown',
      (event) => {
        const target = event.target.closest ? event.target.closest('.button--buy, .card__size, .size-option__label') : null;
        if (!target || target.disabled || target.getAttribute('aria-disabled') === 'true') return;
        if (target.matches('.card__size') && target.getAttribute('aria-pressed') !== 'true' && target.closest('.giftbox__cat.is-locked')) return;
        target.classList.add('is-tapped');
        clearTimeout(target.tapTimer);
        target.tapTimer = setTimeout(() => target.classList.remove('is-tapped'), 450);
      },
      { passive: true }
    );

    /* footer menus fold on small screens */
    if (window.matchMedia('(max-width: 749px)').matches) {
      $$('.footer__details').forEach((details) => details.removeAttribute('open'));
    }

    /* account: show the password reset panel when its form was submitted */
    if (window.location.hash === '#recover' && $('#recover')) $('#recover').scrollIntoView({ block: 'center' });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
